// Accurate cursor picking, shared by click-selection (input.ts) and the hover
// silhouette (renderer). "What is actually under the cursor?" — hit-tested against
// the tight shape each entity is drawn as, resolved front-to-back, so a click lands
// on the sprite you see and a building stays selectable even in a crowd of serfs.

import { buildingFootprint } from '../shared/constants'
import { HEX_SIZE, type GameState } from './state'
import { GROUND_TILT, hexAtWorld, viewRotate } from './render/terrain'

// Unit hit ellipse, tight to the humanoid/animal sprite (body radius UR = 0.2·S,
// head above, feet below). Small enough that neighbours in adjacent hex slots
// (0.5·S away) aren't falsely grabbed.
const U_RX = 0.3 * HEX_SIZE
const U_RY = 0.42 * HEX_SIZE
const U_CY = -0.12 * HEX_SIZE // ellipse centre, lifted from the foot point to the body
// Units standing on a single-hex building (farm workers on their flat farm) shrink
// to this fraction of the ellipse: only a direct body hit picks the worker, and the
// building beneath stays selectable in the gaps between sprites.
const ON_BUILDING_SCALE = 0.62

export type PickResult = { kind: 'unit'; id: number } | { kind: 'building'; id: number }

/** World-space point under a canvas/screen pixel (inlined to avoid an input.ts cycle). */
function screenToWorld(st: GameState, canvas: HTMLCanvasElement, sx: number, sy: number): { x: number; y: number } {
  const z = st.cam.zoom
  const tilt = st.hasElev && st.lift25 ? GROUND_TILT : 1
  // Screen-aligned (rotated) world point — matches st.unitPos and hexAtWorld, which
  // both work in the spun frame. The camera anchor is its rotated position.
  const cam = viewRotate(st, st.cam.x, st.cam.y)
  return {
    x: (sx - canvas.clientWidth / 2) / z + cam.x,
    y: (sy - canvas.clientHeight / 2) / (z * tilt) + cam.y,
  }
}

/**
 * The entity a click at (sx,sy) would select, or null over bare ground. Units win
 * over buildings only where their sprite actually covers the cursor (they always
 * render on top); among overlapping units the front-most (largest y) wins. Reads
 * st.unitPos so the test matches the exact pixels the renderer drew.
 */
export function pickEntity(st: GameState, canvas: HTMLCanvasElement, sx: number, sy: number): PickResult | null {
  const w = screenToWorld(st, canvas, sx, sy)

  // Tile map of building footprints, so units standing on a single-hex building
  // (farm workers) can be given the tighter hit ellipse.
  const bAt = new Map<string, { id: number; single: boolean }>()
  for (const b of st.buildings.values()) {
    const foot = buildingFootprint(b.type, b.q, b.r, b.dir)
    for (const c of foot) {
      const k = c.q + ',' + c.r
      if (!bAt.has(k)) bAt.set(k, { id: b.id, single: foot.length === 1 })
    }
  }

  // On the oblique tilt the sprite is counter-squashed to full height, so its
  // on-screen reach is 1/tilt taller than the foreshortened ground. Stretch the
  // ellipse's y-extent to match, or head/upper-body clicks would fall outside it.
  const ty = st.hasElev && st.lift25 ? 1 / GROUND_TILT : 1
  let unit: number | null = null
  let unitY = -Infinity
  for (const u of st.units.values()) {
    const p = st.unitPos.get(u.id)
    if (!p) continue
    // On a single-hex building only a direct body hit counts — the sprite gaps
    // fall through to the building so both farmer and farm stay selectable.
    const sc = bAt.get(u.q + ',' + u.r)?.single ? ON_BUILDING_SCALE : 1
    const dx = (w.x - p.x) / (U_RX * sc)
    const dy = (w.y - (p.y + U_CY * ty)) / (U_RY * ty * sc)
    if (dx * dx + dy * dy <= 1 && p.y > unitY) {
      unitY = p.y
      unit = u.id
    }
  }
  if (unit !== null) return { kind: 'unit', id: unit }

  const h = hexAtWorld(st, w.x, w.y)
  const building = bAt.get(h.q + ',' + h.r)
  if (building) return { kind: 'building', id: building.id }
  return null
}
